"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  Paperclip,
  FileText,
  File,
  FileSpreadsheet,
  FileImage,
  FileArchive,
  X,
  Loader2,
  Download,
  Pencil,
  Trash2,
  Check,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  uploadAttachment,
  deleteAttachment,
  updateAttachment,
  formatFileSize,
  getFileIconType,
} from "@/lib/api/attachments";
import { getErrorMessage } from "@/lib/api/client";
import type { SimulationAttachment, AttachmentUploadResponse } from "@/types";

// File icon mapping
function FileIcon({ type, className }: { type: string; className?: string }) {
  const iconClass = className || "h-8 w-8";
  
  switch (type) {
    case "pdf":
      return <FileText className={`${iconClass} text-error`} />;
    case "word":
      return <FileText className={`${iconClass} text-info`} />;
    case "excel":
      return <FileSpreadsheet className={`${iconClass} text-success`} />;
    case "image":
      return <FileImage className={`${iconClass} text-laposte-navy`} />;
    case "archive":
      return <FileArchive className={`${iconClass} text-warning`} />;
    default:
      return <File className={`${iconClass} text-muted-foreground`} />;
  }
}

interface LocalAttachment {
  id: number;
  file_name: string;
  file_url: string;
  content_type: string;
  file_size: number;
  title: string | null;
  comment: string | null;
  temp_token: string | null;
  isUploading?: boolean;
  uploadProgress?: number;
}

interface AttachmentsSectionProps {
  versionId: number;
  simulationId?: number | null;
  attachments?: SimulationAttachment[];
  tempAttachments?: LocalAttachment[];
  onTempAttachmentsChange?: (attachments: LocalAttachment[]) => void;
  isReadOnly?: boolean;
  maxAttachments?: number;
  allowedFileTypes?: string[];
  compact?: boolean;
  hideHeader?: boolean;
}

export function AttachmentsSection({
  versionId,
  simulationId,
  attachments = [],
  tempAttachments = [],
  onTempAttachmentsChange,
  isReadOnly = false,
  maxAttachments = 0,
  allowedFileTypes = [],
  compact = false,
  hideHeader = false,
}: AttachmentsSectionProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editComment, setEditComment] = useState("");
  const [deleteId, setDeleteId] = useState<number | null>(null);
  
  // Combine real attachments and temp attachments
  const allAttachments: LocalAttachment[] = [
    ...attachments.map(a => ({ ...a, isUploading: false })),
    ...tempAttachments,
  ];
  
  const currentCount = allAttachments.length;
  const canAddMore = maxAttachments === 0 || currentCount < maxAttachments;
  
  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const response = await uploadAttachment(versionId, file, {
        simulationId: simulationId || undefined,
      });
      // Return both the API response and the original file info
      return { response, file };
    },
    onSuccess: ({ response: data, file }) => {
      if (simulationId) {
        // If simulation exists, refresh the attachments list
        queryClient.invalidateQueries({ queryKey: ["simulation-attachments", simulationId] });
      } else if (onTempAttachmentsChange) {
        // If no simulation yet, add to temp attachments
        const newAttachment: LocalAttachment = {
          id: data.id,
          file_name: data.file_name,
          file_url: data.file_url,
          content_type: file.type,
          file_size: file.size,
          title: null,
          comment: null,
          temp_token: data.temp_token,
        };
        onTempAttachmentsChange([...tempAttachments, newAttachment]);
      }
      toast.success("Fichier uploadé");
    },
    onError: (error) => {
      toast.error("Erreur lors de l'upload", { description: getErrorMessage(error) });
    },
  });
  
  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteAttachment,
    onSuccess: () => {
      if (simulationId) {
        queryClient.invalidateQueries({ queryKey: ["simulation-attachments", simulationId] });
      }
      toast.success("Fichier supprimé");
      setDeleteId(null);
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });
  
  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { title?: string | null; comment?: string | null } }) => 
      updateAttachment(id, data),
    onSuccess: (_, variables) => {
      if (simulationId) {
        queryClient.invalidateQueries({ queryKey: ["simulation-attachments", simulationId] });
      } else if (onTempAttachmentsChange) {
        // Update local temp attachments state
        const updatedAttachments = tempAttachments.map(a => 
          a.id === variables.id 
            ? { ...a, title: variables.data.title ?? a.title, comment: variables.data.comment ?? a.comment }
            : a
        );
        onTempAttachmentsChange(updatedAttachments);
      }
      toast.success("Métadonnées mises à jour");
      setEditingId(null);
    },
    onError: (error) => {
      toast.error("Erreur", { description: getErrorMessage(error) });
    },
  });
  
  // Handle file selection
  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0 || isReadOnly) return;
    
    const fileArray = Array.from(files);
    
    // Check max attachments
    if (maxAttachments > 0 && currentCount + fileArray.length > maxAttachments) {
      toast.error(`Maximum ${maxAttachments} fichier(s) autorisé(s)`);
      return;
    }
    
    // Filter by allowed types if specified
    const validFiles = fileArray.filter(file => {
      if (allowedFileTypes.length === 0) return true;
      const ext = file.name.split('.').pop()?.toLowerCase();
      return ext && allowedFileTypes.includes(ext);
    });
    
    if (validFiles.length !== fileArray.length) {
      toast.warning("Certains fichiers ont été ignorés (type non autorisé)");
    }
    
    // Upload each file
    for (const file of validFiles) {
      await uploadMutation.mutateAsync(file);
    }
  }, [isReadOnly, maxAttachments, currentCount, allowedFileTypes, uploadMutation]);
  
  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isReadOnly) setIsDragging(true);
  }, [isReadOnly]);
  
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);
  
  // Start editing
  const startEdit = (attachment: LocalAttachment) => {
    setEditingId(attachment.id);
    setEditTitle(attachment.title || "");
    setEditComment(attachment.comment || "");
  };
  
  // Save edit
  const saveEdit = () => {
    if (editingId) {
      updateMutation.mutate({
        id: editingId,
        data: {
          title: editTitle || null,
          comment: editComment || null,
        },
      });
    }
  };
  
  // Handle delete
  const handleDelete = (id: number, isTempAttachment: boolean) => {
    if (isTempAttachment && onTempAttachmentsChange) {
      // Remove from temp attachments locally
      onTempAttachmentsChange(tempAttachments.filter(a => a.id !== id));
      // Also delete from server
      deleteMutation.mutate(id);
    } else {
      setDeleteId(id);
    }
  };
  
  // Render single attachment item
  const renderAttachment = (attachment: LocalAttachment, index: number) => {
    const iconType = getFileIconType(attachment.content_type);
    const isEditing = editingId === attachment.id;
    const isTempAttachment = !!attachment.temp_token;
    
    return (
      <motion.div
        key={attachment.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ delay: index * 0.05 }}
        className="group relative flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
      >
        {/* File icon */}
        <div className="flex-shrink-0 p-2 bg-muted rounded-lg">
          <FileIcon type={iconType} className="h-6 w-6" />
        </div>
        
        {/* File info */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="space-y-2">
              <Input
                placeholder="Titre (optionnel)"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="h-8"
              />
              <Textarea
                placeholder="Commentaire (optionnel)"
                value={editComment}
                onChange={(e) => setEditComment(e.target.value)}
                rows={2}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={saveEdit} disabled={updateMutation.isPending}>
                  <Check className="h-3 w-3 mr-1" />
                  Enregistrer
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                  Annuler
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <p className="font-medium text-sm truncate" title={attachment.file_name}>
                  {attachment.title || attachment.file_name}
                </p>
              </div>
              {attachment.title && (
                <p className="text-xs text-muted-foreground truncate">
                  {attachment.file_name}
                </p>
              )}
              {attachment.comment && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {attachment.comment}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {formatFileSize(attachment.file_size)}
              </p>
            </>
          )}
        </div>
        
        {/* Actions */}
        {!isReadOnly && !isEditing && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => window.open(attachment.file_url, "_blank")}
              title="Télécharger"
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => startEdit(attachment)}
              title="Modifier"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => handleDelete(attachment.id, isTempAttachment)}
              title="Supprimer"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        {/* Uploading indicator */}
        {attachment.isUploading && (
          <div className="absolute inset-0 bg-background/80 rounded-lg flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}
      </motion.div>
    );
  };

  // Compact render for inline use
  if (compact) {
    return (
      <div className="space-y-3">
        {/* Upload zone */}
        {!isReadOnly && canAddMore && (
          <div
            className={`
              relative rounded-lg border-2 border-dashed p-4 text-center transition-colors cursor-pointer
              ${isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/50"}
            `}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
            <div className="flex flex-col items-center gap-2">
              {uploadMutation.isPending ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              ) : (
                <Upload className="h-6 w-6 text-muted-foreground" />
              )}
              <p className="text-sm text-muted-foreground">
                {isDragging ? "Déposez le fichier ici" : "Glissez-déposez ou cliquez pour ajouter"}
              </p>
            </div>
          </div>
        )}
        
        {/* Attachments list */}
        <AnimatePresence mode="popLayout">
          {allAttachments.map((attachment, index) => renderAttachment(attachment, index))}
        </AnimatePresence>
        
        {/* Max attachments warning */}
        {maxAttachments > 0 && !canAddMore && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertCircle className="h-4 w-4" />
            <span>Maximum de {maxAttachments} fichier(s) atteint</span>
          </div>
        )}
        
        {/* Delete confirmation */}
        <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer la pièce jointe ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Le fichier sera définitivement supprimé.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteId && deleteMutation.mutate(deleteId)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // Full card render
  return (
    <Card>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CardHeader className="pb-3">
          <CollapsibleTrigger asChild>
            <button className="flex items-center justify-between w-full text-left">
              <div className="flex items-center gap-2">
                <Paperclip className="h-5 w-5 text-muted-foreground" />
                <div>
                  <CardTitle className="text-base">Pièces jointes</CardTitle>
                  <CardDescription>
                    {currentCount} fichier{currentCount !== 1 ? "s" : ""}
                    {maxAttachments > 0 && ` / ${maxAttachments} max`}
                  </CardDescription>
                </div>
              </div>
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          </CollapsibleTrigger>
        </CardHeader>
        
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {/* Upload zone */}
            {!isReadOnly && canAddMore && (
              <div
                className={`
                  relative rounded-lg border-2 border-dashed p-6 text-center transition-colors cursor-pointer
                  ${isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/50"}
                `}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFiles(e.target.files)}
                />
                <div className="flex flex-col items-center gap-2">
                  {uploadMutation.isPending ? (
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  ) : (
                    <Upload className="h-8 w-8 text-muted-foreground" />
                  )}
                  <div>
                    <p className="font-medium">
                      {isDragging ? "Déposez les fichiers ici" : "Glissez-déposez vos fichiers"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      ou cliquez pour sélectionner
                    </p>
                  </div>
                  {allowedFileTypes.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Types acceptés : {allowedFileTypes.join(", ")}
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Attachments list */}
            <AnimatePresence mode="popLayout">
              {allAttachments.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Aucune pièce jointe
                </p>
              ) : (
                <div className="space-y-2">
                  {allAttachments.map((attachment, index) => renderAttachment(attachment, index))}
                </div>
              )}
            </AnimatePresence>
            
            {/* Max attachments warning */}
            {maxAttachments > 0 && !canAddMore && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 rounded-lg bg-muted/50">
                <AlertCircle className="h-4 w-4" />
                <span>Maximum de {maxAttachments} fichier(s) atteint</span>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
      
      {/* Delete confirmation */}
      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la pièce jointe ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le fichier sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
